import React from 'react';
import { Link } from 'react-router-dom';
import {
  Truck, Users, Package, MapPin, Clock,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import styles from './HeroSection.module.css';
import { brandConfig } from '../../../config/brandConfig';
import logoImg from '../../../assets/images/anapurna_logo_transparent.png';

/* ── Carousel slides ── */
const slides = [
  {
    image: "https://images.pexels.com/photos/8945238/pexels-photo-8945238.jpeg",
    tagline: "ESTABLISHED 2015",
    headingLine1: "ANAPURNA SALES",
    headingLine2: "Wholesale",
    highlight: "Redefined",
    subtext: "India's Premiere B2B Fashion Hub Delivering Factory-Direct Quality Since 2015.",
  },
  {
    image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=90&w=1920&auto=format&fit=crop",
    tagline: "LATEST TRENDS",
    headingLine1: "WOMEN'S",
    headingLine2: "Couture",
    highlight: "Collection",
    subtext: "Elegance Meets Wholesale. Discover Our Curated Range of Premium Ladies' Apparel.",
  },
  {
    image: "https://images.pexels.com/photos/428340/pexels-photo-428340.jpeg",
    tagline: "MODERN CLASSICS",
    headingLine1: "MEN'S",
    headingLine2: "Premium",
    highlight: "Essentials",
    subtext: "Sophisticated Menswear Designed for Global Standards. Quality Bulk Manufacturing.",
  },
  {
    image: "https://images.pexels.com/photos/6261878/pexels-photo-6261878.jpeg",
    tagline: "COMFORT & STYLE",
    headingLine1: "KIDS'",
    headingLine2: "Playful",
    highlight: "Fashion",
    subtext: "Durable, Trendy, and Skin-Friendly Garments for the Little Ones at Wholesale Prices.",
  },
  {
    image: "https://images.pexels.com/photos/16199169/pexels-photo-16199169.jpeg",
    tagline: "TIMELESS TRADITION",
    headingLine1: "ETHNIC",
    headingLine2: "Heritage",
    highlight: "Signature",
    subtext: "Celebrate Tradition with Our Exclusive Bulk Indian Ethnic and Fusion Wear.",
  }
];

/* ── Trust strip items ── */
const trustItems = [
  { Icon: Users,   value: brandConfig.trust_stat_retailers, label: brandConfig.trust_stat_retailers_label },
  { Icon: Package, value: brandConfig.trust_stat_orders,    label: brandConfig.trust_stat_orders_label },
  { Icon: MapPin,  value: brandConfig.trust_stat_coverage,  label: brandConfig.trust_stat_coverage_label },
  { Icon: Clock,   value: '24–48 Hrs',                      label: 'Dispatch Time' },
];

const INTERVAL_MS = 4500;

const HeroSection = () => {
  const [current, setCurrent] = React.useState(0);
  const touchStartX = React.useRef(0);
  const touchEndX   = React.useRef(0);
  const total = slides.length;

  /* Auto-advance */
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % total);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [total]);

  const goTo  = idx  => setCurrent((idx + total) % total);
  const prev  = ()   => goTo(current - 1);
  const next  = ()   => goTo(current + 1);

  /* Touch / swipe */
  const onTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
  const onTouchMove  = e => { touchEndX.current   = e.touches[0].clientX; };
  const onTouchEnd   = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
  };

  return (
    <section
      className={styles.hero}
      aria-label="Hero banner"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Editorial Branding Overlay */}
      <div className={styles.heroLogoWrap}>
        <img src={logoImg} alt="Anapurna Sales" className={styles.heroLogo} />
      </div>
      {/* ── Carousel background images (crossfade) ── */}
      <div className={styles.bgImageWrapper} aria-hidden="true">
        {slides.map((slide, idx) => (
          <img
            key={idx}
            src={slide.image}
            alt=""
            className={`${styles.bgImage} ${idx === current ? styles.bgImageActive : ''}`}
          />
        ))}
      </div>

      {/* ── Gradient overlay ── */}
      <div className={styles.gradientOverlay} aria-hidden="true" />

      {/* ── WHOLESALE watermark ── */}
      <div className={styles.bgText} aria-hidden="true">WHOLESALE</div>

      {/* ── Main content — key forces re-animation on slide change ── */}
      <div className={styles.container}>
        <div className={styles.content} key={current}>

          {/* Tagline */}
          <div className={`${styles.taglineWrap} reveal`}>
            <span className={styles.tagline}>{slides[current].tagline}</span>
          </div>

          {/* Heading */}
          <h1 className={styles.heading} key={current}>
            <span className={`${styles.headingLine} ${styles.line1} reveal stagger-1`}>{slides[current].headingLine1}</span>
            <span className={`${styles.headingLine} ${styles.line2} reveal stagger-2`}>{slides[current].headingLine2}</span>
            <span className={`${styles.headingLine} ${styles.line3} reveal stagger-3`}>{slides[current].highlight}</span>
          </h1>

          {/* Slide subtext */}
          <p className={`${styles.subtext} reveal stagger-4`}>{slides[current].subtext}</p>

          {/* CTA Buttons */}
          <div className={`${styles.ctaRow} reveal stagger-5`}>
            <Link to="/enquiry" className={styles.ctaPrimary} aria-label="Get bulk pricing">
              ENQUIRY NOW
            </Link>
            <Link to="/collections" className={styles.ctaSecondary} aria-label="View Collections">
              View Collections
            </Link>
          </div>

          {/* Progress indicators replace dots */}
          <div className={styles.progressControls}>
            {slides.map((_, idx) => (
              <div 
                key={idx} 
                className={styles.progressWrap}
                onClick={() => goTo(idx)}
                role="button"
                aria-label={`Go to slide ${idx + 1}`}
              >
                <div className={`${styles.progressBar} ${idx === current ? styles.animating : ''} ${idx < current ? styles.filled : ''}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Prev / Next arrows ── */}
      {/* ── Mini side arrows ── */}
      <button className={`${styles.miniNavBtn} ${styles.miniNavBtnPrev}`} onClick={prev} aria-label="Previous slide">
        <ChevronLeft size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <button className={`${styles.miniNavBtn} ${styles.miniNavBtnNext}`} onClick={next} aria-label="Next slide">
        <ChevronRight size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>

      {/* ── Floating dispatch badge ── */}
      <div className={styles.floatingBadge} aria-label="Orders dispatched within 24 to 48 hours">
        <span className={styles.badgePulse} aria-hidden="true" />
        <Truck size={16} className={styles.badgeIcon} aria-hidden="true" />
        <span>Dispatch in <strong>24–48 Hours</strong></span>
      </div>

      {/* ── Trust strip ── */}
      <div className={styles.trustStrip} aria-label="Key business statistics">
        <div className={styles.trustContainer}>
          {trustItems.map(({ Icon, value, label }, idx) => (
            <React.Fragment key={label}>
              <div className={styles.trustItem}>
                <Icon size={18} className={styles.trustIcon} aria-hidden="true" />
                <div className={styles.trustText}>
                  <span className={styles.trustValue}>{value}</span>
                  <span className={styles.trustLabel}>{label}</span>
                </div>
              </div>
              {idx < trustItems.length - 1 && (
                <span className={styles.trustDivider} aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
